<template>
  <div
    ref="el"
    :class="{
      'flex-fill flex flex-col w-100': type === 'mobileSearchModal',
    }"
  >
    <!-- search form -->
    <form
      :class="{
        'input-group input-group-lg mb-3 bg-white rounded-pill': type === 'default' || type === 'blog',
        'input-group bg-white rounded-pill': type === 'navbar',
        'input-group mx-n2 mb-5 w-auto': type === 'mobileNavbar',
        'input-group mx-n2 mb-2 w-auto': type === 'mobileSearchModal',
      }"
    >
      <div
        v-if="type === 'navbar' || type === 'blog'"
        class="input-group-prepend items-center pl-3 m-0 hidden xxl:flex"
      >
        <i class="mi-search icon-18 text-primary" />
      </div>
      <div
        v-if="type === 'mobileNavbar' || type === 'mobileSearchModal'"
        class="input-group-prepend m-0"
      >
        <button
          type="submit"
          class="btn p-2 ml-auto border-0"
          aria-label="Hledat"
        >
          <i class="mi-search icon-28 mi-no-mx mi-lh-1" />
        </button>
      </div>
      <input
        v-model="searchText"
        type="text"
        :class="{
          'form-control px-4 text-base shadow-none border-0 rounded-pill bg-white': type === 'default' || type === 'blog',
          'form-control shadow-none border-0 rounded-pill': type === 'navbar',
          'form-control form-control-lg px-2 border-0 text-center font-medium text-base rounded-pill bg-white':
            type === 'mobileNavbar' || type === 'mobileSearchModal',
        }"
        :aria-label="t('find_toothpick')"
        autocomplete="off"
        inputmode="search"
        :placeholder="placeholder !== '' ? placeholder : null"
        required
        @keydown.enter="onEnterPress"
        @input="valueChanged"
        @focus="searchFocused = true"
        @blur="onBlurHandler"
      >
      <div
        v-if="hideWhisperer !== true"
        id="js-word-change-search"
        class="animated-word-search events-none text-muted"
        :class="{ 'hidden': searchFocused === true }"
        :data-text="t('changing_words')"
      >
        <span>{{ t('fruit') }}</span>
      </div>
      <div
        v-if="type === 'navbar'"
        class="input-group-append m-0"
      >
        <button
          class="btn  btn-primary px-5 md:px-3 xl:px-5 font-medium rounded-pill"
          type="button"
          @click="onEnterPress"
        >
          {{ t('search') }}
        </button>
      </div>
      <div
        v-if="type === 'default'"
        class="input-group-append m-0"
      >
        <button
          class="btn btn-primary p-2 rounded-pill"
          aria-label="Search"
          type="button"
          @click="instantSearch"
        >
          <i class="mi-search icon-28" />
        </button>
      </div>
      <!-- close modal -->
      <div
        v-if="type === 'mobileNavbar' || type === 'mobileSearchModal'"
        class="input-group-append m-0"
      >
        <button
          type="button"
          class="btn p-2 mi-no-mx ml-auto border-0"
          data-dismiss="modal"
          aria-label="Close"
          @click="closeButtonHandler"
        >
          <i class="mi-times icon-28 mi-no-mx mi-lh-1" />
        </button>
      </div>
    </form>
    <slot
      name="items"
      :items="items"
    />
  </div>
</template>

<script setup>
import { useDebounceFn } from '@vueuse/core'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  type: {
    type: String,
    default: 'default',
  },

  placeholder: {
    type: String,
    default: '',
  },

  initValue: {
    type: String,
    default: '',
  },

  items: {
    type: Array,
    required: true,
  },

  hideWhisperer: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['valueChanged', 'searchChanged', 'enterPressed', 'closeBtnClicked'])

const { t } = useI18n()

const el = ref()
const wordChange = ref({})
const textSpan = ref({})
const words = ref([])
const index = ref(0)

const searchFocused = ref(false)

const onBlurHandler = (event) => {
  event.target.value.length > 0 ? (searchFocused.value = true) : (searchFocused.value = false)
}

const searchText = ref(props.initValue)
const valueChanged = () => {
  emit('valueChanged', searchText.value)
  searchChanged()
}
const searchChanged = useDebounceFn(
  () => {
    emit('searchChanged', searchText.value)
  },
  100,
  { maxWait: 15_000 }
)
const instantSearch = () => {
  emit('searchChanged', searchText.value)
}
const onEnterPress = (e) => {
  e.preventDefault()
  emit('enterPressed', searchText.value)
}
const closeButtonHandler = () => {
  emit('closeBtnClicked')
}

const changeWord = () => {
  wordChange.value = el.value.querySelector('#js-word-change-search')

  textSpan.value = wordChange.value.querySelector('span')
  words.value = wordChange.value.getAttribute('data-text').split(',')

  wordChange.value.style.width = textSpan.value.offsetWidth

  setInterval(() => {
    wordChange.value.style.width = '0'

    setTimeout(() => {
      textSpan.value.textContent = words.value[index.value]
      changeWidth()
    }, 500)

    index.value++
    if (index.value >= words.value.length) index.value = 0
  }, 3000)
}

const changeWidth = () => {
  wordChange.value.style.width = `${textSpan.value.offsetWidth}px`
}

onMounted(() => {
  if (props.hideWhisperer === false) {
    changeWord()
    changeWidth()
  }
})
</script>

<style lang="scss">
@keyframes writeMark {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
#modal-id-search,
#modal-id-menu {
  .animated-word-search {
    font-weight: 600;
    left: 50%;
    top: 50%;
    transform: translateY(-50%) translateX(-50%);
  }
}
.animated-word-search {
  overflow: hidden;
  transition: width 500ms;
  position: absolute;
  left: 48px;
  font-size: 14px;
  white-space: nowrap;

  &:before {
    content: '';
    background-color: currentColor;
    width: 1px;
    height: 17px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 0px;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    animation-duration: 1s;
    animation-name: writeMark;
  }

  @media screen and (max-width: 1399px) {
    left: 1rem;
  }
}
</style>

<i18n>
  {
    "cs": {
    "changing_words": "ovoce,müsli,oříšky,müsli,kaše,Mixitky,müsli,pytlíky",
    "find_toothpick": "Najdi zobání",
    "fruit": "ovoce",
    "muesli": "müsli",
    "search": "Hledat"
},
    "sk": {
    "changing_words": "ovocie,müsli,oriešky,müsli,kaše,chrumkavé ovocie,Mixitky,müsli,vrecúška",
    "find_toothpick": "Nájdi zobkanie",
    "fruit": "ovocie",
    "muesli": "müsli",
    "search": "Hladat"
},
    "pl": {
    "changing_words": "lio owoce,musli,orzeszki,musli,kasze,Mixitki,musli,saszetki",
    "find_toothpick": "Mam smaka na...",
    "fruit": "lio owoce",
    "muesli": "musli",
    "search": "Szukaj"
},
    "hu": {
    "changing_words": "gyümölcsöt, müzlit, dióféléket, csokoládét, müzlit, kását, ropogós gyümölcsöt, Mixitkát, müzlit, tasakot",
    "find_toothpick": "Keress finomságot",
    "fruit": "gyümölcsöt",
    "muesli": "müzli",
    "search": "Keresés"
},
    "de": {
    "changing_words": "früchte,müsli,nüsse,müsli,porridge,mixitriegel,müsli,snacktaschen",
    "find_toothpick": "Finde deinen Snack",
    "fruit": "Obst",
    "muesli": "Müsli",
    "search": "Suchen"
},
    "at": {
    "changing_words": "früchte,müsli,nüsse,müsli,porridge,mixitriegel,müsli,snacktaschen",
    "find_toothpick": "Finde deinen Snack",
    "fruit": "Obst",
    "muesli": "Müsli",
    "search": "Suchen"
},
    "ro": {
    "changing_words": "fructe, nuci, ciocolată, musli, Mixitka, musli, punguțe",
    "find_toothpick": "Descoperă bunătățile",
    "fruit": "fructe",
    "muesli": "musli",
    "search": "Cautare"
},
    "fr": {
    "changing_words": "fruits, muesli, noix, muesli, porridge, Mixitka, muesli, sachets",
    "find_toothpick": "Découvre nos gourmandises",
    "fruit": "fruits",
    "muesli": "muesli",
    "search": "Rechercher"
},
    "it": {
    "changing_words": "frutta, muesli, noci, muesli, porridge, barrette Mixitka, muesli, bustine",
    "find_toothpick": "Trova degli spuntini",
    "fruit": "frutta",
    "muesli": "muesli",
    "search": "Cerca"
}
  }
</i18n>
